// src/views/Graduation/GraduationStatistics.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import { getInstitutions } from "../../services/institutionService";
import { getSessions } from "../../services/sessionService";
import { getProgramLevels } from "../../services/ProgramLevel";

import {
  batchUpdateGraduationStatistics,
  createGraduationStatistics,
  deleteGraduationStatistics,
  getGraduationStatistics,
} from "../../services/graduationStatisticsService";

import { graduationStatisticsSchema } from "../../validators/graduationStatisticsValidators";
import NumberInput from "../../components/Inputs/NumberInput";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const { Option } = Select;

const GraduationStatistics = () => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // { institution, session, data: [...] }
  const [errs, setErrs] = useState(null);

  const [institutions, setInstitutions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [levels, setLevels] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedSession, setSelectedSession] = useState("Not Selected");

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [expandedRowKey, setExpandedRowKey] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [instRes, sessRes, lvlRes, statRes] = await Promise.all([
        getInstitutions(),
        getSessions(),
        getProgramLevels(),
        getGraduationStatistics(),
      ]);
      setInstitutions(instRes.data?.data || []);
      setSessions(sessRes.data?.data || []);
      setLevels(lvlRes.data?.data || []);
      setStats(statRes.data?.data || []);
      setFilteredStats(statRes.data?.data || []);
    } catch (e) {
      toast.error("Failed to load graduation statistics");
    } finally {
      setLoading(false);
    }
  };

  // Filters
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        (item.institution?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, stats, selectedInstitution, selectedSession]);

  // Group by Institution + Session
  const groupedStats = useMemo(() => {
    const grouped = new Map();
    for (const item of filteredStats) {
      const inst = item.institution;
      const sess = item.session;
      if (!inst || !sess) continue;

      const key = `${inst.id}__${sess.id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          institution: inst,
          session: sess,
          data: [],
          totals: {
            humanities_male_count: 0,
            humanities_female_count: 0,
            sciences_male_count: 0,
            sciences_female_count: 0,
            total: 0,
          },
        });
      }
      const grp = grouped.get(key);
      grp.data.push(item);
      // NOTE: new API fields used here
      const humanities_male = item.humanities_male_count || 0;
      const humanities_female = item.humanities_female_count || 0;
      const sciences_male = item.sciences_male_count || 0;
      const sciences_female = item.sciences_female_count || 0;

      grp.totals.humanities_male_count += humanities_male;
      grp.totals.humanities_female_count += humanities_female;
      grp.totals.sciences_male_count += sciences_male;
      grp.totals.sciences_female_count += sciences_female;

      grp.totals.total +=
        humanities_male + humanities_female + sciences_male + sciences_female;
    }
    return Array.from(grouped.values());
  }, [filteredStats]);

  // Create
  const onCreate = async (payload, resetForm) => {
    setErrs(null);
    try {
      const { data } = await createGraduationStatistics(payload);
      const newRows = data?.data || [];
      const next = [...stats, ...newRows];
      setStats(next);
      setFilteredStats(next);
      toast.success("Graduation statistics added successfully");
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Failed to add graduation statistics"
        );
      }
    }
  };

  // Edit -> open modal with that group's rows
  const handleEdit = (group) => {
    setEditingGroup({
      institution: group.institution,
      session: group.session,
      data: group.data, // array of rows with ids
    });
    setIsModalOpen(true);
  };

  // Batch update
  const onBatchUpdate = async (values, resetForm) => {
    setErrs(null);
    try {
      const { data } = await batchUpdateGraduationStatistics(values);
      const updated = data?.data || [];

      const mapUpdated = new Map(updated.map((r) => [r.id, r]));
      const replaced = stats.map((row) =>
        mapUpdated.has(row.id) ? mapUpdated.get(row.id) : row
      );
      const newOnes = updated.filter((r) => !stats.find((x) => x.id === r.id));
      const finalState = [...replaced, ...newOnes];

      setStats(finalState);
      setFilteredStats(finalState);

      toast.success("Graduation statistics updated successfully!");
      setIsModalOpen(false);
      setEditingGroup(null);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Failed to update graduation statistics"
        );
      }
    }
  };

  // Delete single stat row
  const confirmDelete = (row) => {
    setRowToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const onDelete = async () => {
    const original = [...stats];
    const next = original.filter((s) => s.id !== rowToDelete.id);
    setStats(next);
    setFilteredStats(next);

    try {
      await deleteGraduationStatistics(rowToDelete.id);
      toast.success("Deleted successfully!");
    } catch (e) {
      toast.error("Failed to delete. Try again later.");
      setStats(original);
      setFilteredStats(original);
    } finally {
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setEditingGroup(null);
    setRowToDelete(null);
    setErrs(null);
  };

  const columns = [
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: "institution",
            width: 320,
            render: (institution) => (
              <span className="font-semibold text-lg">{institution?.name}</span>
            ),
          },
        ]
      : []),
    {
      title: "Year",
      dataIndex: ["session", "name"],
      width: 160,
    },
    {
      title: "Male (Humanities)",
      dataIndex: ["totals", "humanities_male_count"],
      width: 160,
    },
    {
      title: "Female (Humanities)",
      dataIndex: ["totals", "humanities_female_count"],
      width: 160,
    },
    {
      title: "Male (Sciences)",
      dataIndex: ["totals", "sciences_male_count"],
      width: 160,
    },
    {
      title: "Female (Sciences)",
      dataIndex: ["totals", "sciences_female_count"],
      width: 160,
    },
    {
      title: "Total",
      dataIndex: ["totals", "total"],
      width: 160,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Graduation Statistics">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Graduation Statistics"
        subtitle1="Graduation Statistics"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Graduation Statistics"
            count={groupedStats.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Graduation Statistics"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={groupedStats}
            expandable={{
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: (expanded, record) => {
                setExpandedRowKey(expanded ? record.key : null);
              },
              expandedRowRender: (record) => (
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Level</th>
                      <th className="text-left p-2">Male(Humanities)</th>
                      <th className="text-left p-2">Female(Humanities)</th>
                      <th className="text-left p-2">Male(Sciences)</th>
                      <th className="text-left p-2">Female(Sciences)</th>
                      <th className="text-left p-2">Total</th>
                      <th className="text-left p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-amber-50">
                    {record.data.map((item) => (
                      <tr key={item.id}>
                        <td className="p-2 text-sm">
                          {item.academic_program_level?.name || "—"}
                        </td>
                        <td className="p-2 text-sm">
                          {item.humanities_male_count}
                        </td>
                        <td className="p-2 text-sm">
                          {item.humanities_female_count}
                        </td>
                        <td className="p-2 text-sm">
                          {item.sciences_male_count}
                        </td>
                        <td className="p-2 text-sm">
                          {item.sciences_female_count}
                        </td>
                        <td className="p-2 text-sm">
                          {(item.humanities_male_count || 0) +
                            (item.humanities_female_count || 0) +
                            (item.sciences_male_count || 0) +
                            (item.sciences_female_count || 0)}
                        </td>
                        <td className="p-1">
                          <Tooltip title="Delete">
                            <button
                              onClick={() => confirmDelete(item)}
                              className="text-red-500 p-1"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ),
            }}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => setSelectedInstitution(value)}
                    showSearch
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    <Option value={"Not Selected"}>
                      Filter by Institution
                    </Option>
                    {institutions.map((inst) => (
                      <Option key={inst.id} value={inst.id}>
                        {inst.name}
                      </Option>
                    ))}
                  </Select>
                )}

                <Select
                  multiple={false}
                  id="sessions"
                  defaultValue={selectedSession}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedSession(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value={"Not Selected"}>Filter by Year</Option>
                  {sessions.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {editingGroup
                    ? "Edit Graduation Statistics"
                    : "Add Graduation Statistics"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={1200}
          >
            <Formik
              initialValues={{
                institution_id: editingGroup
                  ? editingGroup.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                session_id: editingGroup ? editingGroup.session?.id : "",
                academic_program_level_stats: editingGroup?.data?.map(
                  (row) => ({
                    id: row.id,
                    academic_program_level_id:
                      row.academic_program_level?.id || "",
                    male_count: row.humanities_male_count ?? "",
                    female_count: row.humanities_female_count ?? "",
                    sciences_male_count: row.sciences_male_count ?? "",
                    sciences_female_count: row.sciences_female_count ?? "",
                  })
                ) || [
                  {
                    id: "",
                    academic_program_level_id: "",
                    male_count: "",
                    female_count: "",
                    sciences_male_count: "",
                    sciences_female_count: "",
                  },
                ],
              }}
              enableReinitialize
              validationSchema={graduationStatisticsSchema}
              onSubmit={(values, { resetForm }) => {
                if (editingGroup) {
                  onBatchUpdate(values, resetForm);
                } else {
                  onCreate(values, resetForm);
                }
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => (
                <Form className="space-y-5 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-3 grid-cols-2">
                    {/* Institution */}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Institution <span className="text-red-500">*</span>
                      </label>
                      {isInstAdmin ? (
                        <input
                          type="hidden"
                          name="institution_id"
                          value={
                            editingGroup
                              ? editingGroup?.institution?.id
                              : user?.institution?.id
                          }
                        />
                      ) : (
                        <>
                          <Select
                            name="institution_id"
                            value={values.institution_id || undefined}
                            onChange={(val) => {
                              setFieldValue("institution_id", val);
                              setFieldValue("session_id", "");
                              setFieldValue("academic_program_level_stats", [
                                {
                                  academic_program_level_id: "",
                                  male_count: "",
                                  female_count: "",
                                  sciences_male_count: "",
                                  sciences_female_count: "",
                                },
                              ]);
                            }}
                            placeholder="Select Institution"
                            className="w-full"
                            disabled={!!editingGroup}
                          >
                            {institutions.map((inst) => (
                              <Option key={inst.id} value={inst.id}>
                                {inst.name}
                              </Option>
                            ))}
                          </Select>
                          {errors.institution_id && touched.institution_id && (
                            <div className="text-red-500 text-xs">
                              {errors.institution_id}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Session */}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="session_id"
                        value={values.session_id || undefined}
                        onChange={(val) => {
                          setFieldValue("session_id", val);
                          setFieldValue("academic_program_level_stats", [
                            {
                              academic_program_level_id: "",
                              male_count: "",
                              female_count: "",
                              sciences_male_count: "",
                              sciences_female_count: "",
                            },
                          ]);
                        }}
                        placeholder="Select Year"
                        className="w-full"
                        disabled={!!editingGroup}
                      >
                        {sessions.map((s) => (
                          <Option key={s.id} value={s.id}>
                            {s.name}
                          </Option>
                        ))}
                      </Select>
                      {errors.session_id && touched.session_id && (
                        <div className="text-red-500 text-xs">
                          {errors.session_id}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Rows */}
                  <FieldArray name="academic_program_level_stats">
                    {({ push, remove }) => (
                      <div className="space-y-4">
                        {values.academic_program_level_stats.map((_, index) => {
                          const currentLevelId =
                            values.academic_program_level_stats[index]
                              .academic_program_level_id;

                          const usedInForm = values.academic_program_level_stats
                            .map((r, i) => r.academic_program_level_id)
                            .filter((id, i) => i !== index);

                          const existingForCombo = stats
                            .filter(
                              (r) =>
                                r.institution?.id === values.institution_id &&
                                r.session?.id === values.session_id
                            )
                            .map((r) => r.academic_program_level?.id);

                          const excludedIds = new Set([
                            ...usedInForm,
                            ...existingForCombo,
                          ]);

                          const availableLevels = levels.filter(
                            (lvl) =>
                              !excludedIds.has(lvl.id) ||
                              lvl.id === currentLevelId
                          );

                          return (
                            <div
                              key={index}
                              className="grid md:grid-cols-6 gap-4 items-end border border-gray-200 p-4 rounded-md bg-gray-50"
                            >
                              {/* Level */}
                              <div className="col-span-2">
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                  Level <span className="text-red-500">*</span>
                                </label>
                                <Select
                                  value={
                                    values.academic_program_level_stats[index]
                                      .academic_program_level_id || undefined
                                  }
                                  onChange={(val) =>
                                    setFieldValue(
                                      `academic_program_level_stats[${index}].academic_program_level_id`,
                                      val
                                    )
                                  }
                                  placeholder="Select Program Level"
                                  className="w-full"
                                  disabled={
                                    !values.institution_id || !values.session_id
                                  }
                                >
                                  {availableLevels.map((lvl) => (
                                    <Option key={lvl.id} value={lvl.id}>
                                      {lvl.name}
                                    </Option>
                                  ))}
                                </Select>
                                {errors.academic_program_level_stats &&
                                  errors.academic_program_level_stats[index]
                                    ?.academic_program_level_id &&
                                  touched.academic_program_level_stats?.[index]
                                    ?.academic_program_level_id && (
                                    <div className="text-red-500 text-xs">
                                      {
                                        errors.academic_program_level_stats[
                                          index
                                        ].academic_program_level_id
                                      }
                                    </div>
                                  )}
                              </div>

                              {/* Humanities Male */}
                              <NumberInput
                                name={`academic_program_level_stats[${index}].male_count`}
                                label="Male Count (Humanities)"
                                placeholder="e.g. 120"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Humanities Female */}
                              <NumberInput
                                name={`academic_program_level_stats[${index}].female_count`}
                                label="Female Count (Humanities)"
                                placeholder="e.g. 130"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Sciences Male */}
                              <NumberInput
                                name={`academic_program_level_stats[${index}].sciences_male_count`}
                                label="Male Count (Sciences)"
                                placeholder="e.g. 120"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Sciences Female */}
                              <NumberInput
                                name={`academic_program_level_stats[${index}].sciences_female_count`}
                                label="Female Count (Sciences)"
                                placeholder="e.g. 130"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Remove new rows only */}
                              {!values.academic_program_level_stats[index]
                                .id ? (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 justify-self-end"
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              ) : (
                                <Tooltip title="Cannot delete existing level here">
                                  <button
                                    type="button"
                                    disabled
                                    className="text-gray-400 justify-self-end cursor-not-allowed"
                                  >
                                    <FiTrash2 size={18} />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          );
                        })}

                        {/* Add Level */}
                        {levels.length >
                          values.academic_program_level_stats.length && (
                          <button
                            type="button"
                            className="mt-2 text-blue-600"
                            onClick={() =>
                              push({
                                academic_program_level_id: "",
                                male_count: "",
                                female_count: "",
                                sciences_male_count: "",
                                sciences_female_count: "",
                              })
                            }
                            disabled={
                              !values.institution_id || !values.session_id
                            }
                          >
                            + Add Level
                          </button>
                        )}
                      </div>
                    )}
                  </FieldArray>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          {/* Delete modal */}
          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDelete}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={
              rowToDelete
                ? `Please confirm that you want to delete ${rowToDelete.academic_program_level?.name} stats for ${rowToDelete.institution?.name}`
                : ""
            }
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default GraduationStatistics;
